import { useState, type ChangeEvent } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DocumentSchema,
  DocumentTypeSchema,
  type DocumentDto,
  type DocumentTypeDto,
} from '@taqon/contracts';
import { z } from 'zod';
import { getData, newIdempotencyKey, patchJson, postForm } from '../api/client';
import { FileRules, oversizeMessage, useUploadLimit } from './upload-limit';
import { hasCapability, useSession } from '../session/session';
import { FormDialog, StatusChip } from '../ui/forms';
import { EmptyState, ErrorState, LoadingState, Section } from '../ui/page';
import { PersonName } from '../ui/person';
import { businessToday, daysBetween } from './dates';

export const DOCUMENTS = '/api/v1/documents';
/** How often the list is refreshed while a document is being scanned (SDD §17.5). */
export const SCAN_POLL_MS = 3000;

export const documentsKey = (recordType: string, recordId: string) => [
  'documents',
  recordType,
  recordId,
];

/** One record's documents, refreshed while any of them is still being scanned. */
export function useRecordDocuments(recordType: string, recordId: string, enabled: boolean) {
  return useQuery({
    queryKey: documentsKey(recordType, recordId),
    enabled,
    queryFn: () =>
      getData(
        `${DOCUMENTS}?ownerRecordType=${encodeURIComponent(recordType)}&ownerRecordId=${encodeURIComponent(recordId)}`,
        z.array(DocumentSchema),
      ),
    refetchInterval: (query) =>
      (query.state.data ?? []).some((d) => d.status === 'pending_scan') ? SCAN_POLL_MS : false,
  });
}

/** The Document Types the session may use on one record. */
export function useRecordDocumentTypes(recordType: string, recordId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['document-types', recordType, recordId],
    enabled,
    queryFn: () =>
      getData(
        `${DOCUMENTS}/types?recordType=${encodeURIComponent(recordType)}&ownerRecordId=${encodeURIComponent(recordId)}`,
        z.array(DocumentTypeSchema),
      ),
  });
}

/**
 * The documents of one record (FRD-CORE-019, CORE-020; SDD §17), for any record page.
 *
 * What is listed is decided by the API: access follows the owning record, and a confidential
 * document the session may not see is simply absent (FRD-CORE-021, FRD-HRP-019). Uploading and
 * replacing are offered only when the page says the session may attach (`canAttach`) and it holds
 * `platform.document:create`; the API decides regardless. The same people may correct a current
 * document's title, number and dates without a new file (Edit, CLR-45). A download is a plain link to the API,
 * which streams the file and records the access (FRD-CORE-022). While a document is being scanned
 * the list refreshes itself until the scan finishes.
 */
export function DocumentsPanel({
  recordType,
  recordId,
  canAttach,
  title = 'Documents',
}: {
  recordType: string;
  recordId: string;
  canAttach: boolean;
  title?: string;
}) {
  const session = useSession();
  const canView = hasCapability(session.data, 'platform.document:view');
  const mayUpload = canAttach && hasCapability(session.data, 'platform.document:create');
  const [uploading, setUploading] = useState(false);
  const [replacing, setReplacing] = useState<DocumentDto | null>(null);
  const [editing, setEditing] = useState<DocumentDto | null>(null);
  const [showEarlier, setShowEarlier] = useState(false);

  const documents = useRecordDocuments(recordType, recordId, canView);
  const types = useRecordDocumentTypes(recordType, recordId, canView);

  if (!canView) return null;
  const all = documents.data ?? [];
  const current = all.filter((d) => d.status !== 'superseded');
  const earlier = all.filter((d) => d.status === 'superseded');
  const missing = (types.data ?? []).filter(
    (type) =>
      type.requiredForRecord &&
      !current.some(
        (d) =>
          d.documentTypeId === type.id && (d.status === 'available' || d.status === 'pending_scan'),
      ),
  );
  const today = businessToday(session.data?.businessTimezone ?? 'Asia/Dubai');

  return (
    <Section
      title={title}
      actions={
        mayUpload ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            disabled={(types.data ?? []).length === 0}
            onClick={(event) => {
              event.currentTarget.focus();
              setUploading(true);
            }}
          >
            Upload
          </Button>
        ) : undefined
      }
    >
      {documents.isPending && <LoadingState label="Loading documents" />}
      {documents.isError && (
        <Box sx={{ p: 2 }}>
          <ErrorState message={documents.error.message} />
        </Box>
      )}
      {documents.data !== undefined && (
        <Stack spacing={1.5} sx={{ p: all.length === 0 && missing.length === 0 ? 0 : 2 }}>
          {missing.length > 0 && (
            <Alert severity="info" variant="outlined">
              Required documents not yet on file: {missing.map((t) => t.name).join(', ')}.
            </Alert>
          )}
          {mayUpload && types.data !== undefined && types.data.length === 0 && (
            <Alert severity="info" variant="outlined">
              No document types are configured for this kind of record yet. An administrator
              configures them under Administration › Document types.
            </Alert>
          )}
          {all.length === 0 ? (
            <EmptyState
              title="No documents"
              {...(mayUpload ? { detail: 'Upload the first document for this record.' } : {})}
            />
          ) : (
            <>
              <DocumentTable
                label={`${title} of this record`}
                documents={current}
                today={today}
                mayUpload={mayUpload}
                onReplace={setReplacing}
                onEdit={setEditing}
              />
              {earlier.length > 0 && (
                <Box>
                  <Button
                    size="small"
                    aria-expanded={showEarlier}
                    onClick={() => {
                      setShowEarlier((shown) => !shown);
                    }}
                  >
                    {showEarlier
                      ? 'Hide earlier versions'
                      : `Show ${String(earlier.length)} earlier ${earlier.length === 1 ? 'version' : 'versions'}`}
                  </Button>
                  {showEarlier && (
                    <DocumentTable
                      label="Earlier versions"
                      documents={earlier}
                      today={today}
                      mayUpload={false}
                      onReplace={setReplacing}
                      onEdit={setEditing}
                    />
                  )}
                </Box>
              )}
            </>
          )}
        </Stack>
      )}
      {uploading && (
        <UploadDialog
          recordType={recordType}
          recordId={recordId}
          types={types.data ?? []}
          replacing={null}
          onClose={() => {
            setUploading(false);
          }}
        />
      )}
      {editing !== null && (
        <EditDetailsDialog
          key={editing.id}
          document={editing}
          recordType={recordType}
          recordId={recordId}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
      {replacing !== null && (
        <UploadDialog
          recordType={recordType}
          recordId={recordId}
          types={types.data ?? []}
          replacing={replacing}
          onClose={() => {
            setReplacing(null);
          }}
        />
      )}
    </Section>
  );
}

function DocumentTable({
  label,
  documents,
  today,
  mayUpload,
  onReplace,
  onEdit,
}: {
  label: string;
  documents: readonly DocumentDto[];
  today: string;
  mayUpload: boolean;
  onReplace: (document: DocumentDto) => void;
  onEdit: (document: DocumentDto) => void;
}) {
  return (
    <Table size="small" aria-label={label}>
      <TableHead>
        <TableRow>
          <TableCell>Type</TableCell>
          <TableCell>Title</TableCell>
          <TableCell>Number</TableCell>
          <TableCell>Issued</TableCell>
          <TableCell>Expiry</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Uploaded</TableCell>
          <TableCell>Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {documents.map((document) => (
          <TableRow key={document.id}>
            <TableCell>
              {document.documentTypeName}
              {document.confidential && (
                <Typography variant="caption" component="div" color="text.secondary">
                  Confidential
                </Typography>
              )}
            </TableCell>
            <TableCell sx={{ overflowWrap: 'anywhere' }}>
              {document.title}
              <Typography variant="caption" component="div" color="text.secondary">
                {document.originalFileName}
              </Typography>
            </TableCell>
            <TableCell>{document.documentNumber ?? '—'}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{document.issueDate ?? '—'}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              {document.expiryDate ?? '—'}
              {document.expiryDate !== null && document.status === 'available' && (
                <ExpiryHint expiryDate={document.expiryDate} today={today} />
              )}
            </TableCell>
            <TableCell>
              <StatusChip status={document.status} />
              <StatusNote status={document.status} />
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              {document.uploadedByName === null ? (
                'System'
              ) : (
                <PersonName name={document.uploadedByName} userId={document.uploadedBy} />
              )}
              <Typography variant="caption" component="div" color="text.secondary">
                {new Date(document.uploadedAt).toLocaleString('en-GB')}
              </Typography>
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              <Stack direction="row" spacing={1}>
                {document.status === 'available' && (
                  <Link
                    href={`${DOCUMENTS}/${document.id}/content`}
                    aria-label={`Download ${document.title}`}
                  >
                    Download
                  </Link>
                )}
                {mayUpload && document.replacementAllowed && document.status === 'available' && (
                  <Button
                    size="small"
                    aria-label={`Replace ${document.title}`}
                    onClick={(event) => {
                      event.currentTarget.focus();
                      onReplace(document);
                    }}
                  >
                    Replace
                  </Button>
                )}
                {mayUpload && document.status === 'available' && document.origin === 'uploaded' && (
                  <Button
                    size="small"
                    aria-label={`Edit details of ${document.title}`}
                    onClick={(event) => {
                      event.currentTarget.focus();
                      onEdit(document);
                    }}
                  >
                    Edit
                  </Button>
                )}
              </Stack>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** "expired", "expires today" or "expires in n days", from today in the business time zone. */
export function ExpiryHint({ expiryDate, today }: { expiryDate: string; today: string }) {
  const days = daysBetween(today, expiryDate);
  const text =
    days < 0
      ? `expired ${String(-days)} ${-days === 1 ? 'day' : 'days'} ago`
      : days === 0
        ? 'expires today'
        : `expires in ${String(days)} ${days === 1 ? 'day' : 'days'}`;
  return (
    <Typography
      variant="caption"
      component="div"
      color={days < 0 ? 'error' : days <= 30 ? 'warning.main' : 'text.secondary'}
    >
      {text}
    </Typography>
  );
}

function StatusNote({ status }: { status: DocumentDto['status'] }) {
  const note =
    status === 'pending_scan'
      ? 'Being scanned for malware; it can be downloaded once the scan is clean.'
      : status === 'quarantined'
        ? 'Failed the malware scan and was quarantined. It can never be downloaded.'
        : status === 'scan_failed'
          ? 'Could not be scanned. It cannot be downloaded; upload the file again.'
          : null;
  if (note === null) return null;
  return (
    <Typography variant="caption" component="div" color="text.secondary" sx={{ maxWidth: 240 }}>
      {note}
    </Typography>
  );
}

/**
 * Upload a new document, or a replacement of `replacing` (which keeps its type). The fields are
 * appended before the file, as the API requires (SDD §17.4). The idempotency key is fixed for the
 * life of the dialog, so a retried submission returns the same document (SDD §7.5).
 */
function UploadDialog({
  recordType,
  recordId,
  types,
  replacing,
  onClose,
}: {
  recordType: string;
  recordId: string;
  types: readonly DocumentTypeDto[];
  replacing: DocumentDto | null;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [idempotencyKey] = useState(newIdempotencyKey);
  const [typeCode, setTypeCode] = useState(replacing?.documentTypeCode ?? '');
  const [title, setTitle] = useState(replacing?.title ?? '');
  const [documentNumber, setDocumentNumber] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const limit = useUploadLimit();

  const type = types.find((t) => t.code === typeCode);
  const expiryTracked = type?.expiryTracked ?? replacing?.expiryTracked ?? false;

  const send = useMutation({
    mutationFn: () => {
      const form = new FormData();
      if (replacing === null) {
        form.append('ownerRecordType', recordType);
        form.append('ownerRecordId', recordId);
      }
      form.append('documentTypeCode', typeCode);
      form.append('title', title.trim());
      if (documentNumber.trim() !== '') form.append('documentNumber', documentNumber.trim());
      if (issueDate !== '') form.append('issueDate', issueDate);
      if (expiryTracked) form.append('expiryDate', expiryDate);
      // The file goes last: the API authorizes from the fields before it stores anything.
      if (file !== null) form.append('file', file, file.name);
      return postForm(
        replacing === null ? DOCUMENTS : `${DOCUMENTS}/${replacing.id}/replace`,
        form,
        DocumentSchema,
        session.data?.csrfToken ?? '',
        { idempotencyKey },
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: documentsKey(recordType, recordId) });
      onClose();
    },
  });

  const canSubmit =
    typeCode !== '' &&
    title.trim() !== '' &&
    file !== null &&
    oversizeMessage(file, limit) === null &&
    (!expiryTracked || expiryDate !== '');
  const accept = (type?.allowedContentTypes ?? []).join(',');

  return (
    <FormDialog
      open
      title={replacing === null ? 'Upload a document' : `Replace ${replacing.title}`}
      submitLabel={replacing === null ? 'Upload' : 'Replace'}
      canSubmit={canSubmit}
      submitting={send.isPending}
      error={send.isError ? send.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        send.mutate();
      }}
    >
      {replacing !== null && (
        <Alert severity="info" variant="outlined">
          The current version stays in place until the new file has been scanned; it is then kept as
          an earlier version. Nothing is deleted.
        </Alert>
      )}
      {replacing === null ? (
        <TextField
          select
          required
          label="Document type"
          value={typeCode}
          onChange={(e) => {
            setTypeCode(e.target.value);
          }}
        >
          {types.map((option) => (
            <MenuItem key={option.code} value={option.code}>
              {option.name}
              {option.confidentialityCategory !== null ? ' (confidential)' : ''}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <TextField label="Document type" value={replacing.documentTypeName} disabled />
      )}
      <TextField
        label="Title"
        required
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
        }}
        slotProps={{ htmlInput: { maxLength: 200 } }}
      />
      <Box>
        <Typography component="label" htmlFor="document-file" variant="body2" sx={{ mb: 0.5 }}>
          File *
        </Typography>
        <Box
          component="input"
          id="document-file"
          type="file"
          aria-label="File"
          aria-required
          {...(accept !== '' ? { accept } : {})}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setFile(event.target.files?.[0] ?? null);
          }}
          sx={{ display: 'block', mt: 0.5, maxWidth: '100%' }}
        />
        <FileRules
          accepted={type === undefined ? null : type.allowedContentTypes.map(shortType).join(', ')}
          file={file}
          limit={limit}
        />
      </Box>
      <DetailFields
        expiryTracked={expiryTracked}
        documentNumber={documentNumber}
        issueDate={issueDate}
        expiryDate={expiryDate}
        onDocumentNumber={setDocumentNumber}
        onIssueDate={setIssueDate}
        onExpiryDate={setExpiryDate}
      />
    </FormDialog>
  );
}

/** Document number and issue date for every type; the expiry date only where the type tracks it. */
function DetailFields({
  expiryTracked,
  documentNumber,
  issueDate,
  expiryDate,
  onDocumentNumber,
  onIssueDate,
  onExpiryDate,
}: {
  expiryTracked: boolean;
  documentNumber: string;
  issueDate: string;
  expiryDate: string;
  onDocumentNumber: (value: string) => void;
  onIssueDate: (value: string) => void;
  onExpiryDate: (value: string) => void;
}) {
  return (
    <>
      <TextField
        label="Document number"
        value={documentNumber}
        onChange={(e) => {
          onDocumentNumber(e.target.value);
        }}
        slotProps={{ htmlInput: { maxLength: 100 } }}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Issue date"
          type="date"
          value={issueDate}
          onChange={(e) => {
            onIssueDate(e.target.value);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
          fullWidth
        />
        {expiryTracked && (
          <TextField
            label="Expiry date"
            type="date"
            required
            value={expiryDate}
            onChange={(e) => {
              onExpiryDate(e.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />
        )}
      </Stack>
    </>
  );
}

/**
 * Corrects a current document's title, number and dates without a new file (CLR-45). The file,
 * type and owning record stay as they are; the change is audited by the API.
 */
function EditDetailsDialog({
  document,
  recordType,
  recordId,
  onClose,
}: {
  document: DocumentDto;
  recordType: string;
  recordId: string;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(document.title);
  const [documentNumber, setDocumentNumber] = useState(document.documentNumber ?? '');
  const [issueDate, setIssueDate] = useState(document.issueDate ?? '');
  const [expiryDate, setExpiryDate] = useState(document.expiryDate ?? '');

  const send = useMutation({
    mutationFn: () =>
      patchJson(
        `${DOCUMENTS}/${document.id}`,
        {
          title: title.trim(),
          documentNumber: documentNumber.trim() === '' ? null : documentNumber.trim(),
          issueDate: issueDate === '' ? null : issueDate,
          expiryDate: document.expiryTracked && expiryDate !== '' ? expiryDate : null,
        },
        DocumentSchema,
        session.data?.csrfToken ?? '',
        document.version,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: documentsKey(recordType, recordId) });
      onClose();
    },
  });

  const canSubmit = title.trim() !== '' && (!document.expiryTracked || expiryDate !== '');

  return (
    <FormDialog
      open
      title={`Edit details of ${document.title}`}
      submitLabel="Save"
      canSubmit={canSubmit}
      submitting={send.isPending}
      error={send.isError ? send.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        send.mutate();
      }}
    >
      <Alert severity="info" variant="outlined">
        Changes the details only; the file stays as it is. To upload a new file, use Replace.
      </Alert>
      <TextField label="Document type" value={document.documentTypeName} disabled />
      <TextField label="File" value={document.originalFileName} disabled />
      <TextField
        label="Title"
        required
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
        }}
        slotProps={{ htmlInput: { maxLength: 200 } }}
      />
      <DetailFields
        expiryTracked={document.expiryTracked}
        documentNumber={documentNumber}
        issueDate={issueDate}
        expiryDate={expiryDate}
        onDocumentNumber={setDocumentNumber}
        onIssueDate={setIssueDate}
        onExpiryDate={setExpiryDate}
      />
      {!document.expiryTracked && (
        <Typography variant="caption" color="text.secondary">
          This document type does not track an expiry date. An administrator can switch that on
          under Administration › Document types.
        </Typography>
      )}
    </FormDialog>
  );
}

const SHORT_TYPES: Readonly<Record<string, string>> = {
  'application/pdf': 'PDF',
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
  'image/gif': 'GIF',
  'image/webp': 'WEBP',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word (.docx)',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel (.xlsx)',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint (.pptx)',
  'application/msword': 'Word (.doc)',
  'application/vnd.ms-excel': 'Excel (.xls)',
  'text/plain': 'Text (.txt)',
  'text/csv': 'CSV',
  'audio/webm': 'Audio (.webm)',
  'audio/ogg': 'Audio (.ogg)',
  'audio/mp4': 'Audio (.m4a)',
  'audio/mpeg': 'Audio (.mp3)',
  'audio/wav': 'Audio (.wav)',
};

/** A content type as people name it. */
export function shortType(contentType: string): string {
  return SHORT_TYPES[contentType] ?? contentType;
}
